import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGamesAndLeagues } from '../services/gameLeagueService';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  //Typography,
  //Grid,
  Paper,
  styled,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
//import type { RootState } from '../store/store';
import { setGame, setLeague, selectGameFilter, selectLeagueFilter } from '../store/slices/filterSlice';

// Game and League interfaces
export interface Game {
  id: string;
  name: string;
  code: string; // 'PoE1' or 'PoE2'
  isActive: boolean;
}

export interface League {
  id: string;
  name: string;
  code: string; // e.g., 'standard', 'dawn-of-the-hunt'
  gameId: string; // Associated with a specific game
  isActive: boolean;
  startDate?: string;
  endDate?: string;
}

// Props interface for the component
export interface GameLeagueSelectorProps {
  onGameChange?: (gameCode: string) => void;
  onLeagueChange?: (leagueCode: string) => void;
  className?: string;
}

// Styled components

// Styled components
const SelectorContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
}));

/**
 * GameLeagueSelector component
 * Provides dropdowns for selecting game and league in ExileGuard Dashboard
 */
const GameLeagueSelector: React.FC<GameLeagueSelectorProps> = ({ onGameChange, onLeagueChange, className }) => {
  const dispatch = useDispatch();

  // Get game and league from Redux store
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // Local state for game and league data
  const [games, setGames] = useState<Game[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [availableLeagues, setAvailableLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch games and leagues from API (with caching)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { games: fetchedGames, leagues: fetchedLeagues } = await getGamesAndLeagues();
        setGames(fetchedGames);
        setLeagues(fetchedLeagues);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch game/league data:', err);
        setError('Failed to load game and league data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up refresh interval (every 10 minutes)
    const intervalId = setInterval(fetchData, 10 * 60 * 1000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Update available leagues when game changes or when leagues data is loaded
  useEffect(() => {
    if (games.length === 0 || leagues.length === 0) return;

    // Find the selected game
    const gameObject = games.find(game => game.code === selectedGame);

    // Filter leagues for the selected game
    const filteredLeagues = leagues.filter(league => league.gameId === gameObject?.id);

    setAvailableLeagues(filteredLeagues);

    // Clear selected league if it doesn't exist in the new game's leagues
    const leagueExists = filteredLeagues.some(league => league.code === selectedLeague);
    if (!leagueExists && filteredLeagues.length > 0) {
      // Auto-select the first available league
      const newLeague = filteredLeagues[0].code;
      console.log(`Auto-selecting league: ${newLeague} for game: ${selectedGame}`);
      dispatch(setLeague(newLeague));

      if (onLeagueChange) {
        onLeagueChange(newLeague);
      }
    } else if (!selectedLeague && filteredLeagues.length > 0) {
      // If no league is selected at all (initial load), select the first one
      const newLeague = filteredLeagues[0].code;
      console.log(`Initial league selection: ${newLeague} for game: ${selectedGame}`);
      dispatch(setLeague(newLeague));

      if (onLeagueChange) {
        onLeagueChange(newLeague);
      }
    }
  }, [games, leagues, selectedGame, selectedLeague, dispatch, onLeagueChange]);

  // Handle game change
  const handleGameChange = (event: SelectChangeEvent<string>) => {
    const gameCode = event.target.value as string;
    dispatch(setGame(gameCode));

    if (onGameChange) {
      onGameChange(gameCode);
    }
  };

  // Handle league change
  const handleLeagueChange = (event: SelectChangeEvent<string>) => {
    const leagueCode = event.target.value as string;
    dispatch(setLeague(leagueCode));

    if (onLeagueChange) {
      onLeagueChange(leagueCode);
    }
  };

  return (
    <SelectorContainer className={className}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: { xs: 'auto', sm: '150px' } }}>
          <FormControl fullWidth>
            <InputLabel id='game-select-label'>Game</InputLabel>
            <Select labelId='game-select-label' id='game-select' value={selectedGame} label='Game' onChange={handleGameChange}>
              {loading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : error ? (
                <MenuItem disabled>Error loading data</MenuItem>
              ) : (
                games.map(game => (
                  <MenuItem key={game.id} value={game.code}>
                    {game.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ flex: 1, minWidth: { xs: 'auto', sm: '150px' } }}>
          <FormControl fullWidth>
            <InputLabel id='league-select-label'>League</InputLabel>
            <Select
              labelId='league-select-label'
              id='league-select'
              value={selectedLeague}
              label='League'
              onChange={handleLeagueChange}
              disabled={loading || error !== null || availableLeagues.length === 0}
            >
              {loading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : error ? (
                <MenuItem disabled>Error loading data</MenuItem>
              ) : availableLeagues.length === 0 ? (
                <MenuItem disabled>No leagues available</MenuItem>
              ) : (
                availableLeagues.map((league: League) => (
                  <MenuItem key={league.id} value={league.code}>
                    {league.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </SelectorContainer>
  );
};

export default GameLeagueSelector;
